import { DateFormat } from '@equip.health/ui';

import {
  GetBodyTraceOrderResponse,
  StatusHistoryEntry,
} from '~/store/services/clinicalData';

import { formattedDateFromISO } from './date.util';
import { EquipScaleOrderStatus } from '../constants/patients.constants';
export const getStatusHistoryDateString = (
  bodyTraceOrderData: GetBodyTraceOrderResponse,
) => {
  const statusLabel = bodyTraceOrderData?.status.label;
  const deviceStatusLabel = bodyTraceOrderData?.device.status.label;
  const entry = bodyTraceOrderData?.statusHistory.find(
    (entry) =>
      entry.status.label === statusLabel ||
      entry.status.label === deviceStatusLabel,
  );

  const timestamp = entry?.timestamp || '';
  return formattedDateFromISO(timestamp, DateFormat.shortMonthDayYear);
};

export const getDeviceStatus = (
  bodyTraceOrderData: GetBodyTraceOrderResponse,
): DeviceOrderStatus => {
  const statusLabel = bodyTraceOrderData?.status.label;

  if (
    statusLabel === EquipScaleOrderStatus.REQUESTED ||
    statusLabel === EquipScaleOrderStatus.CONFIRMED
  ) {
    return EquipScaleOrderStatus.ORDERED;
  }

  return statusLabel as DeviceOrderStatus;
};
export const getDeviceStatusForActive = (
  bodyTraceOrderData: GetBodyTraceOrderResponse,
): DeviceOrderStatus | null =>
  bodyTraceOrderData?.device.status.label === 'Active'
    ? (bodyTraceOrderData?.device.status.label as DeviceOrderStatus)
    : null;

export const getDeviceOrderDate = (
  bodyTraceOrderData: GetBodyTraceOrderResponse,
  formattedDate: string,
): string => {
  const statusLabel = bodyTraceOrderData?.status.label;
  const deviceStatusLabel = bodyTraceOrderData?.device.status.label;
  const deviceDate = bodyTraceOrderData?.device.dateActive;
  const formattedDeviceDate =
    deviceDate &&
    formattedDateFromISO(deviceDate, DateFormat.shortMonthDayYear);

  if (
    statusLabel === EquipScaleOrderStatus.DELIVERED &&
    deviceStatusLabel === EquipScaleOrderStatus.ACTIVE
  ) {
    return `Activated on ${formattedDeviceDate}`;
  }
  const displayStatus =
    statusLabel === EquipScaleOrderStatus.REQUESTED ||
    statusLabel === EquipScaleOrderStatus.CONFIRMED
      ? EquipScaleOrderStatus.ORDERED
      : statusLabel;
  return `${displayStatus} on ${formattedDate}`;
};

export const getDeviceImei = (bodyTraceOrderData: GetBodyTraceOrderResponse) =>
  bodyTraceOrderData?.device.id;
export const getStatusHistory = (
  bodyTraceOrderData: GetBodyTraceOrderResponse,
) => {
  let statusHistory = bodyTraceOrderData?.statusHistory ?? [];

  if (bodyTraceOrderData?.device.status.label === 'Active') {
    statusHistory = [
      ...statusHistory,
      {
        status: { code: 'ACTIVE', label: 'Active' },
        timestamp: bodyTraceOrderData?.device.dateActive,
      },
    ];
  }

  return statusHistory;
};

export const getStatusHistoryDateByStatusLabel = (
  statusHistory: StatusHistoryEntry[],
  statusLabel: string,
) => {
  const entry = statusHistory.find(
    (entry) => entry.status.label === statusLabel,
  );
  return entry
    ? formattedDateFromISO(entry.timestamp, DateFormat.fullMonthDayYearWithTime)
    : '';
};

export const mapStatusHistoriesToStatusLabels = (
  statusHistory: StatusHistoryEntry[],
) => {
  const relevantStatuses = [
    EquipScaleOrderStatus.REQUESTED,
    EquipScaleOrderStatus.SHIPPED,
    EquipScaleOrderStatus.DELIVERED,
    EquipScaleOrderStatus.ACTIVE,
  ];
  const desiredOrder = [
    EquipScaleOrderStatus.ORDERED,
    EquipScaleOrderStatus.SHIPPED,
    EquipScaleOrderStatus.DELIVERED,
    EquipScaleOrderStatus.ACTIVE,
  ];

  const statusLabels = statusHistory
    .filter((entry) =>
      relevantStatuses.includes(entry.status.label as EquipScaleOrderStatus),
    )
    .map((entry) =>
      entry.status.label === EquipScaleOrderStatus.REQUESTED
        ? EquipScaleOrderStatus.ORDERED
        : (entry.status.label as EquipScaleOrderStatus),
    );

  const uniqueStatusLabels = Array.from(new Set(statusLabels));

  return uniqueStatusLabels.sort((a, b) => {
    return desiredOrder.indexOf(a) - desiredOrder.indexOf(b);
  });
};

export const deriveAddressCardPropsfromBodyTraceOrderResponse = (
  bodyTraceOrderData: GetBodyTraceOrderResponse,
) => {
  return {
    addressLine1: bodyTraceOrderData?.shipToAddress.street,
    addressLine2: bodyTraceOrderData?.shipToAddress?.street2,
    addressTypeLabel: bodyTraceOrderData?.addressTypeLabel,
    city: bodyTraceOrderData?.shipToAddress.city,
    email: bodyTraceOrderData?.addressEmail,
    name: `${bodyTraceOrderData?.addressFirstName} ${bodyTraceOrderData?.addressLastName}`,
    phoneNumber: bodyTraceOrderData?.addressPhoneNumber,
    state: bodyTraceOrderData?.shipToAddress.state,
    zipcode: bodyTraceOrderData?.shipToAddress.postalCode,
  };
};

export const isOrderPendingConfirmation = (
  bodyTraceOrderData: GetBodyTraceOrderResponse,
) => {
  const statusHistoryEntry = bodyTraceOrderData?.statusHistory[0]?.status;
  return (
    statusHistoryEntry?.label === null && statusHistoryEntry?.code === null
  );
};
export const isOrderCanceledOrFailed = (status: string): boolean => {
  return (
    status === EquipScaleOrderStatus.ORDER_CANCELED ||
    status === EquipScaleOrderStatus.DELIVERY_FAILED ||
    status === EquipScaleOrderStatus.PROCESSING_FAILED
  );
};

export const getActiveOrder = (
  bodyTraceOrderData: GetBodyTraceOrderResponse[],
): GetBodyTraceOrderResponse => {
  return bodyTraceOrderData?.find(
    (order) => order.device.status.label !== EquipScaleOrderStatus.TERMINATED,
  );
};
